import React from 'react';
import { ArrowRight, ArrowLeft, Check, Search, Globe, Zap, FileText, MapPin, Link2, BarChart3, Target, TrendingUp, Building2, Briefcase, Sparkles, Monitor, Mic } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CookieBanner from '../components/layout/CookieBanner';

const whySEOWins = [
  'Buyers trust organic search results',
  'High-intent traffic converts better',
  'Lower cost per lead over time',
  'Long-term visibility and authority'
];

const seoStrategies = [
  'Target keywords buyers actually use',
  'Align content with search intent',
  'Improve on-page conversion rates',
  'Generate qualified leads, not just traffic'
];

const services = [
  {
    icon: Search,
    title: 'SEO Strategy & Keyword Research',
    description: 'We identify high-intent keywords that attract people ready to buy, not just browse.'
  },
  {
    icon: FileText,
    title: 'On-Page SEO',
    description: 'We optimize pages for search engines and users—titles, meta descriptions, headers, content, and internal linking.'
  },
  {
    icon: Zap,
    title: 'Technical SEO',
    description: 'We improve site speed, crawlability, indexing, and structure so search engines can rank you higher.'
  },
  {
    icon: FileText,
    title: 'Content Optimization & Creation',
    description: 'SEO-driven content designed to rank, educate, and convert visitors into leads.'
  },
  {
    icon: MapPin,
    title: 'Local SEO',
    description: 'Get found by customers in your area with optimized Google Business Profiles and local search strategies.'
  },
  {
    icon: Link2,
    title: 'Link Building & Authority Growth',
    description: 'We build high-quality backlinks that increase domain authority and trust.'
  },
  {
    icon: BarChart3,
    title: 'SEO Tracking & Reporting',
    description: "Clear reporting on rankings, traffic, leads, and performance—not vanity metrics."
  }
];

const process = [
  {
    step: '01',
    title: 'SEO Audit & Opportunity Analysis',
    description: 'We uncover technical issues, ranking gaps, and growth opportunities.'
  },
  {
    step: '02',
    title: 'Keyword & Intent Mapping',
    description: 'We map keywords to the right pages based on search intent and funnel stage.'
  },
  {
    step: '03',
    title: 'On-Page & Technical Optimization',
    description: 'We optimize your site for speed, structure, and relevance.'
  },
  {
    step: '04',
    title: 'Content & Authority Building',
    description: 'We create and optimize content that ranks and builds trust.'
  },
  {
    step: '05',
    title: 'Ongoing Optimization & Growth',
    description: 'SEO is never "set and forget." We continuously improve and scale results.'
  }
];

const platforms = [
  { icon: Globe, name: 'Google Search' },
  { icon: MapPin, name: 'Google Maps & Local Pack' },
  { icon: Search, name: 'Bing' },
  { icon: Mic, name: 'Mobile & Voice Search' }
];

const whoIsFor = [
  { icon: Briefcase, title: 'Service-based businesses' },
  { icon: MapPin, title: 'Local & national companies' },
  { icon: Building2, title: 'B2B & SaaS brands' },
  { icon: Target, title: 'Companies tired of relying only on ads' },
  { icon: TrendingUp, title: 'Businesses looking for long-term growth' }
];

const results = [
  'Increased visibility for high-intent searches',
  'More qualified organic traffic',
  'Lower cost per lead over time',
  'Stronger brand authority',
  'Long-term, compounding growth'
];

const SEO = () => {
  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex flex-col justify-center overflow-hidden bg-[#f8f7f4] pt-32 pb-16">
          {/* Gradient blob background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-0 right-0 w-[80%] h-[120%] opacity-50"
              style={{
                background: `
                  radial-gradient(ellipse 70% 50% at 75% 20%, rgba(34, 197, 94, 0.3) 0%, transparent 50%),
                  radial-gradient(ellipse 60% 50% at 85% 50%, rgba(59, 130, 246, 0.25) 0%, transparent 50%),
                  radial-gradient(ellipse 50% 60% at 65% 70%, rgba(16, 185, 129, 0.2) 0%, transparent 50%)
                `,
                filter: 'blur(60px)',
                transform: 'rotate(-10deg)'
              }}
            />
          </div>

          <div className="relative z-10 px-6 md:px-12 lg:px-24">
            {/* Back link */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-12"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>

            <div className="max-w-4xl">
              <span className="text-sm uppercase tracking-widest text-gray-500 mb-4 block">Search Engine Optimization</span>
              <h1 className="text-[clamp(2.5rem,5vw,4.5rem)] font-light leading-[1.1] text-black mb-6">
                SEO That Drives Traffic, Leads, and{' '}
                <span className="font-medium">Long-Term Growth</span>
              </h1>
              <p className="text-xl text-gray-700 max-w-2xl mb-4">
                Get Found by People Actively Searching for What You Offer
              </p>
              <p className="text-lg text-gray-600 max-w-2xl mb-8">
                We build SEO strategies that attract high-intent traffic, increase visibility, and convert searches into customers. 
                SEO isn't about chasing keywords—it's about showing up when buyers are actively looking.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors"
                >
                  Get a Free SEO Audit <ArrowRight size={18} />
                </a>
                <a
                  href="#process"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-black text-black rounded-full font-medium hover:bg-black hover:text-white transition-colors"
                >
                  See Our SEO Process
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why SEO Wins */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium mb-6">
                  Why SEO Still <span className="text-green-400">Wins</span>
                </h2>
                <p className="text-gray-400 text-lg mb-8">
                  Paid ads stop the moment you stop spending. SEO compounds.
                </p>
                <ul className="space-y-4 mb-8">
                  {whySEOWins.map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check size={18} className="text-green-400 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg text-white font-medium">
                  If you want predictable, scalable growth, SEO is essential.
                </p>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-green-500/20 to-blue-500/20 rounded-3xl p-8">
                  <div className="space-y-4">
                    <div className="bg-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400">Organic Traffic</span>
                        <span className="text-green-400">+156%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className="w-4/5 h-full bg-green-500 rounded-full"></div>
                      </div>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400">Keyword Rankings</span>
                        <span className="text-green-400">Top 10</span>
                      </div>
                      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className="w-3/4 h-full bg-green-500 rounded-full"></div>
                      </div>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400">Cost Per Lead</span>
                        <span className="text-green-400">-45%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className="w-1/3 h-full bg-green-500 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our SEO Advantage */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-sm uppercase tracking-widest text-green-600 mb-4 block">Our Advantage</span>
                <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                  Intent-Driven, <span className="text-green-600">Conversion-Focused</span> SEO
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  We don't just focus on rankings—we focus on search intent and conversions.
                </p>
                <div className="mb-8">
                  <p className="text-gray-700 font-medium mb-4">Our SEO strategies are built to:</p>
                  <ul className="space-y-3">
                    {seoStrategies.map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check size={18} className="text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-lg text-black font-medium">
                  Every optimization is tied to real business outcomes.
                </p>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-3xl p-8">
                  <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Search className="w-6 h-6 text-green-600" />
                      <span className="text-gray-400">google.com</span>
                    </div>
                    <div className="bg-gray-100 rounded-lg p-4 mb-4">
                      <div className="h-3 bg-gray-300 rounded w-3/4 mb-2"></div>
                      <div className="h-2 bg-gray-200 rounded w-full"></div>
                    </div>
                    <div className="space-y-3">
                      <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">#1</span>
                          <span className="text-green-700 font-medium text-sm">Your Business</span>
                        </div>
                        <div className="h-2 bg-green-200 rounded w-full"></div>
                      </div>
                      <div className="border border-gray-200 rounded-lg p-3 opacity-60">
                        <div className="h-2 bg-gray-300 rounded w-3/4 mb-1"></div>
                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                      </div>
                      <div className="border border-gray-200 rounded-lg p-3 opacity-40">
                        <div className="h-2 bg-gray-300 rounded w-2/3 mb-1"></div>
                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SEO Services */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="mb-16">
              <h2 className="text-[clamp(3rem,8vw,8rem)] font-light text-gray-300 leading-none tracking-tight">
                <span className="block">SEO</span>
                <span className="block ml-[10%]">SERVICES</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all group">
                  <service.icon className="w-10 h-10 text-gray-400 group-hover:text-green-500 mb-4 transition-colors" />
                  <h3 className="text-xl font-medium text-black mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section id="process" className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium mb-6">
                Our SEO Process
              </h2>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {process.map((item, index) => (
                <div key={index} className="relative">
                  <span className="text-6xl font-light text-gray-700">{item.step}</span>
                  <h3 className="text-lg font-medium text-white mt-2 mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                  {index < process.length - 1 && (
                    <div className="hidden md:block absolute top-8 right-0 w-full h-px bg-gray-700 -mr-4"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Platforms */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Platforms & Search Engines We Optimize For
              </h2>
              <p className="text-lg text-gray-600">
                We optimize for how people actually search today.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-6">
              {platforms.map((platform, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl px-8 py-6 flex items-center gap-4 hover:shadow-md transition-shadow">
                  <platform.icon className="w-8 h-8 text-green-600" />
                  <span className="text-gray-800 font-medium">{platform.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who Is This For */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Who SEO Is For
              </h2>
              <p className="text-lg text-gray-600">
                Our SEO services are ideal for:
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
              {whoIsFor.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <item.icon className="w-10 h-10 text-gray-700 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-black">{item.title}</h3>
                </div>
              ))}
            </div>

            <p className="text-center text-lg text-gray-700 font-medium">
              If you want consistent inbound leads, SEO works.
            </p>
          </div>
        </section>

        {/* Results */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-12">
                Results You Can Expect
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.map((result, index) => (
                  <div key={index} className="bg-gray-50 rounded-2xl p-6 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check size={20} className="text-green-600" />
                    </div>
                    <p className="text-gray-800 font-medium text-left">{result}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-light mb-6">
                Ready to Rank Where{' '}
                <span className="font-medium">Buyers Are Searching?</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Let's build an SEO strategy that drives real business growth.
              </p>
              <a
                href="mailto:hello@gomobyle.com"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black rounded-full font-medium hover:bg-gray-200 transition-colors mb-4"
              >
                Book a Free SEO Audit <ArrowRight size={18} />
              </a>
              <p className="text-gray-500 text-sm">
                No contracts. No gimmicks. Just proven SEO.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default SEO;
